// PostForm.tsx

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Eye, ArrowRight, ArrowLeft, AlertCircle } from "lucide-react";
import useCategories from "../../../../hooks/features/categories/useCategories";
import { PostFormData, Post } from "../../../../types/features/Post";
import RichTextEditor from "../../../ui/RichTextEditor";
import useToken from "../../../../hooks/features/auth/useToken";
import { uploadService } from "../../../../services/uploadService";
import useAuth from "../../../../hooks/features/auth/useAuth";
import { alertError } from "../../../../helpers/alertError";
import usePostForm from "../../../../hooks/features/posts/usePostForm";

interface PostFormProps {
  initialData?: Post;
  isEdit?: boolean;
  onSubmit: (data: PostFormData, categoryId: string) => Promise<void>;
  isLoading?: boolean;
}

type Step = "french" | "english" | "seo";

export default function PostForm({ initialData, onSubmit, isLoading = false }: PostFormProps) {
  const navigate = useNavigate();
  const { token } = useToken();
  const { categories } = useCategories();
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState(initialData?.category_id || "");
  const [currentStep, setCurrentStep] = useState<Step>("french");

  // 📝 Utilisation du hook usePostForm
  const draft = usePostForm(initialData);

  const [formData, setFormData] = useState<PostFormData>(
    draft.draftData || {
      title: "",
      title_en: "",
      excerpt: "",
      excerpt_en: "",
      content: "",
      content_en: "",
      featured_image: null,
      tags: [],
      status: "draft",
      published_at: null,
      seo_title: null,
      seo_description: null,
      product_name: "blog",
      product_name_en: "blog",
    }
  );

  // Mettre à jour le formulaire quand les données du brouillon changent
  useEffect(() => {
    if (draft.draftData) {
      setFormData(draft.draftData);
    }
  }, [draft.draftData]);

  // Mettre à jour le formulaire quand initialData change
  useEffect(() => {
    if (initialData) {
      const data = {
        title: initialData.title,
        title_en: initialData.title_en,
        excerpt: initialData.excerpt,
        excerpt_en: initialData.excerpt_en,
        content: initialData.content,
        content_en: initialData.content_en,
        featured_image: initialData.featured_image,
        tags: initialData.tags,
        status: initialData.status,
        published_at: initialData.published_at,
        seo_title: initialData.seo_title || null,
        seo_description: initialData.seo_description || null,
        product_name: initialData.product_name || "blog",
        product_name_en: initialData.product_name_en || "blog",
      };
      setFormData(data);
      setSelectedCategory(initialData.category_id);
      draft.updateDraftData(data);
    }
  }, [initialData]);

  // ✅ Quand la catégorie change, récupérer son product_name
  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    
    // Trouver la catégorie sélectionnée
    const selectedCat = categories.find(cat => cat.id === categoryId);
    
    if (selectedCat) {
      // Mettre à jour product_name avec celui de la catégorie
      const newData = {
        ...formData,
        product_name: selectedCat.product_name || "blog",
        product_name_en: selectedCat.product_name_en || "blog",
      };
      setFormData(newData);
      draft.updateDraftData(newData);
    }
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    if (!user?.id) {
      alertError("Veuillez vous connecter avant de publier un article");
      return "";
    }
    try {
      const result = await uploadService.uploadFile(token, {
        type: "image",
        reason: "blog_post",
        file,
        user_id: user?.id,
      });
      return result.url;
    } catch (error) {
      alertError("Erreur lors du téléchargement de l'image");
      return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const newData = { ...formData, [name]: value };
    setFormData(newData);
    draft.updateDraftData(newData);
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tags = e.target.value
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag);
    const newData = { ...formData, tags };
    setFormData(newData);
    draft.updateDraftData(newData);
  };

  // 📝 Sauvegarder en brouillon
  const saveDraft = async () => {
    if (!selectedCategory) {
      alertError("Veuillez sélectionner une catégorie");
      return;
    }

    const dataToSubmit = { ...formData };
    dataToSubmit.status = "draft";
    dataToSubmit.published_at = null;

    draft.saveDraft(dataToSubmit);
    await onSubmit(dataToSubmit, selectedCategory);
  };

  // ✅ Publier l'article
  const publishPost = async () => {
    if (!selectedCategory) {
      alertError("Veuillez sélectionner une catégorie");
      return;
    }

    if (!formData.title.trim()) {
      alertError("Le titre français est obligatoire");
      return;
    }

    if (!formData.title_en.trim()) {
      alertError("Le titre anglais est obligatoire");
      return;
    }

    const dataToSubmit = { ...formData };
    dataToSubmit.status = "published";
    dataToSubmit.published_at = new Date().toISOString();

    draft.clearDraft();
    await onSubmit(dataToSubmit, selectedCategory);
  };

  const nextStep = () => {
    if (currentStep === "french") setCurrentStep("english");
    else if (currentStep === "english") setCurrentStep("seo");
  };

  const prevStep = () => {
    if (currentStep === "english") setCurrentStep("french");
    else if (currentStep === "seo") setCurrentStep("english");
  };

  const isFrenchValid = () => {
    return formData.title.trim() !== "" && selectedCategory !== "";
  };

  const isEnglishValid = () => {
    return formData.title_en.trim() !== "";
  };

  const isDraft = initialData?.status === "draft" || formData.status === "draft";

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      {/* 📋 Barre d'état du brouillon */}
      <div className="flex flex-wrap gap-3 items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-3">
          {draft.hasUnsavedChanges && (
            <span className="flex items-center gap-1 text-yellow-700 bg-yellow-50 px-3 py-1 rounded-full text-sm">
              <AlertCircle className="w-4 h-4" />
              Modifications non sauvegardées
            </span>
          )}
          {draft.isAutoSaving && (
            <span className="text-blue-600 bg-blue-50 px-3 py-1 rounded-full text-sm">
              💾 Sauvegarde auto...
            </span>
          )}
          {draft.hasDraft && !draft.hasUnsavedChanges && (
            <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
              ✅ Brouillon sauvegardé
            </span>
          )}
        </div>
        <div className="text-sm text-gray-500">
          {draft.lastSavedAt && (
            <span>Dernière sauvegarde : {new Date(draft.lastSavedAt).toLocaleTimeString()}</span>
          )}
        </div>
      </div>

      {/* 📊 Statut de l'article */}
      {initialData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-4">
            <span className="font-medium text-gray-700">Statut :</span>
            {initialData.status === "published" ? (
              <span className="text-green-600 font-medium">✅ Publié</span>
            ) : (
              <span className="text-yellow-600 font-medium">📝 Brouillon</span>
            )}
            {initialData.published_at && (
              <span className="text-gray-500 text-sm">
                Publié le : {new Date(initialData.published_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* 📝 Formulaire */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {currentStep === "french" && (
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-2">
              <h2 className="text-lg font-semibold text-gray-900">Étape 1 : Français</h2>
              <p className="text-sm text-gray-500">Remplissez le contenu en français</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Sélectionner une catégorie</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {/* Afficher le produit associé */}
              {selectedCategory && categories.find(cat => cat.id === selectedCategory)?.product_name && (
                <p className="text-xs text-gray-500 mt-1">
                  Produit associé : {categories.find(cat => cat.id === selectedCategory)?.product_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre (FR) *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Ex: Comment booster votre productivité"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Extrait (FR)</label>
              <textarea
                name="excerpt"
                value={formData.excerpt || ""}
                onChange={handleChange}
                rows={3}
                placeholder="Court résumé de l'article"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenu (FR)</label>
              <RichTextEditor
                onImageUpload={handleImageUpload}
                value={formData.content || ""}
                onChange={(value) => {
                  const newData = { ...formData, content: value };
                  setFormData(newData);
                  draft.updateDraftData(newData);
                }}
                placeholder="Écrivez votre contenu en français..."
              />
            </div>
          </div>
        )}

        {currentStep === "english" && (
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-2">
              <h2 className="text-lg font-semibold text-gray-900">Étape 2 : English</h2>
              <p className="text-sm text-gray-500">Fill in the English content</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title (EN) *</label>
              <input
                type="text"
                name="title_en"
                value={formData.title_en}
                onChange={handleChange}
                required
                placeholder="Ex: How to boost your productivity"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt (EN)</label>
              <textarea
                name="excerpt_en"
                value={formData.excerpt_en || ""}
                onChange={handleChange}
                rows={3}
                placeholder="Short summary of the article"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content (EN)</label>
              <RichTextEditor
                onImageUpload={handleImageUpload}
                value={formData.content_en || ""}
                onChange={(value) => {
                  const newData = { ...formData, content_en: value };
                  setFormData(newData);
                  draft.updateDraftData(newData);
                }}
                placeholder="Write your content in English..."
              />
            </div>
          </div>
        )}

        {currentStep === "seo" && (
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-2">
              <h2 className="text-lg font-semibold text-gray-900">Étape 3 : SEO & Tags</h2>
              <p className="text-sm text-gray-500">Optimisez pour les moteurs de recherche</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                value={formData.tags.join(", ")}
                onChange={handleTagsChange}
                placeholder="Séparer les tags par des virgules"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Ex: technologie, business, startup</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO - Titre</label>
              <input
                type="text"
                name="seo_title"
                value={formData.seo_title || ""}
                onChange={handleChange}
                placeholder="Titre pour les moteurs de recherche"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO - Description</label>
              <textarea
                name="seo_description"
                value={formData.seo_description || ""}
                onChange={handleChange}
                rows={3}
                placeholder="Description pour les moteurs de recherche"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        )}
      </div>

      {/* 🔘 Actions */}
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={() => {
            if (draft.hasUnsavedChanges && !confirm("Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter ?")) {
              return;
            }
            draft.clearDraft();
            navigate(-1);
          }}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Annuler
        </button>

        <div className="flex gap-3">
          {currentStep !== "french" && (
            <button
              type="button"
              onClick={prevStep}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Précédent
            </button>
          )}

          {currentStep !== "seo" ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={
                currentStep === "french" ? !isFrenchValid() : currentStep === "english" ? !isEnglishValid() : false
              }
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Suivant
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="flex gap-3">
              {/* 📝 Enregistrer en brouillon */}
              <button
                type="button"
                onClick={saveDraft}
                disabled={isLoading || !selectedCategory}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {isLoading ? "Enregistrement..." : isDraft ? "Mettre à jour le brouillon" : "Enregistrer en brouillon"}
              </button>

              {/* ✅ Publier */}
              <button
                type="button"
                onClick={publishPost}
                disabled={isLoading || !isFrenchValid() || !isEnglishValid() || !selectedCategory}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Eye className="w-4 h-4" />
                {isLoading ? "Publication..." : isDraft ? "Publier maintenant" : "Publier"}
              </button>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}